import { HelpOutline, Add as AddIcon } from '@mui/icons-material';
import { Alert, Button, Modal, ToggleButton, ToggleButtonGroup, LinearProgress, Card } from '@mui/material';
import { addDays, differenceInDays, format, subYears } from 'date-fns';
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import IntegrationModal from '../components/Integrations/IntegrationModal';
import OrgAgreementsModal from '../components/Modals/OrgAgreementsModal';
import UserAgreementsModal from '../components/Modals/UserAgreementsModal';
import SubscriptionFailed from '../components/Payments/SubscriptionFailed';
import SubscriptionSuccessful from '../components/Payments/SubscriptionSuccessful';
import Register from '../components/Register/Register';
import ACTIONS from '../store/action';
import { Actions as PROFILEACTIONS } from '../store/actions/profileActions';
import { DashboardButton, DashboardIcon, AddButton } from '../styledComponents/GlobalStyle';
import SubscriptionEnded from '../components/Register/SubscriptionEnded';
import Treemap from '../components/Dashboard/Treemap';
import ColoumnChart from '../components/Dashboard/ColoumnChart';
import DonutChart from '../components/Dashboard/DonutChart';
import NetZeroChart from '../components/Dashboard/NetZeroChart';
import { generateShades } from '../components/utils/PaletteCreator';
import DashboardCard from '../components/Dashboard/DashboardCard';
import DashboardModal from '../components/Dashboard/DashboardModal';
import Select from 'react-select';
import { PieChart, TreeMapChart } from '../components/Dashboard/DashboardCharts';
import { Waveform } from '@uiball/loaders';
import { Actions as DASHBOARDACTIONS } from '../store/actions/dashboardActions';
import { Actions as USAGEACTIONS } from '../store/actions/usageActions';
import { useHistory } from 'react-router';
import SkeletonDashboard from '../components/Dashboard/SkeletonDashboard';
import { getReportingYearDates, getMultiYearChartDates } from '../components/Common/ReusableComponents/Reusable';
import {
  closestCenter,
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

const ConnectStripeAccountModal = ({ showStripeConnect, connectStripeAccount }) => {
  const handleClose = () => null;
  const [loading, setLoading] = useState(false);

  if (!showStripeConnect) {
    return null;
  } else {
    return (
      <div>
        <Modal open={showStripeConnect} onClose={handleClose}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h4 className='modal-title'>Please connect your account</h4>
                </div>
              </div>
              <div className='modal-body' style={{ overflow: 'auto' }}>
                <p style={{ fontWeight: 'bold' }}>
                  To complete your Ambassador set up, please follow this link to create a Stripe Connect account. This will
                  enable payment of commissions.
                </p>
                <button
                  className='btn btn-success'
                  onClick={async () => {
                    setLoading(true);
                    await connectStripeAccount();
                    setLoading(false);
                  }}>
                  {loading && <i className='fa fa-spinner fa-spin' style={{ marginRight: 5 }} />}
                  Connect Stripe Account
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
};

const Dashboard = ({
  firstName,
  branding,
  showSubscriptionSuccesfulModal,
  stripeCheckoutFailed,
  stripeCheckoutSuccess,
  hlp,
  tourID,
  hasSubscriptionActive,
  ssoUser,
  list,
  fetchedOrganisations,
  ambassadorAdmin,
  stripeAccountId,
  currentOrg,
  orgDetails,
  directUser,
  whiteLabelAdmin,
  blockBtns,
  connectStripeAccount,
  accountType,
  daysUntilTestOrgExpire,
  helpHeroBtnStyles,
  slaAgreementConfirmed,
  eulaAgreementConfirmed,
  dashboardYear,
  latestUsage,
  years,
  dashboardComponents,
  sumValues,
  dashboardData,
  loader,
  currentYear,
  usageTotals,
  getUsageTotals,
  getTargets,
  orgTargets,
  SBTNetZero,
  Dashboard,
  canView,
  canEdit,
  canCreate,
  canDelete,
  canManage,
  getDashboardComponents,
  saveComponents,
  excludedFacilities,
  orgMetrics,
  orgCurrency,
  showLoader,
}) => {
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [showStripeConnect, setShowStripeConnect] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [year, setYear] = useState(dashboardYear || currentYear);
  const [userSelectedYear, setUserSelectedYear] = useState(false);
  const history = useHistory();

  // Initialize dates based on organisation's reporting year end
  const initialDates = getReportingYearDates(currentYear, orgDetails);
  const [fromDate, setFromDate] = useState(initialDates.fromDate);
  const [toDate, setToDate] = useState(initialDates.toDate);

  const [chipSelected, setChipSelected] = useState('1 Year');
  const [dataType, setDataType] = useState('market');
  const [dashboardModal, setDashboardModal] = useState({ show: false, id: null });
  const [components, setComponents] = useState(
    dashboardComponents ? [...dashboardComponents?.filter((c) => c.type != 'Cards')] : []
  );
  const [cardComponents, setCardComponents] = useState(
    dashboardComponents ? [...dashboardComponents?.filter((c) => c.type === 'Cards')] : []
  );
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const font = branding?.fonts?.[0]?.font?.body;

  const isCalendarYearOrg = useMemo(() => {
    return orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;
  }, [orgDetails]);

  const currentRevenue = useMemo(() => orgMetrics?.find((m) => m.year == year)?.originalTurnover, [orgDetails, year]);

  const intensityValue = useMemo(
    () => (dashboardData?.totalEmissions?.toFixed(2) / (currentRevenue / 1000000))?.toFixed(2),
    [dashboardData, currentRevenue]
  );

  // Create custom year labels based on reporting period
  const createYearLabels = (startYear, endYear, orgDetails) => {
    const reportingYearEndMonth = orgDetails?.reportingMonth || orgDetails?.reportingYearEndMonth;
    const reportingYearEndDay = orgDetails?.reportingDay || orgDetails?.reportingYearEndDay;

    // If reporting year end is December 31st, use simple year labels
    if (reportingYearEndMonth == 12 && reportingYearEndDay == 31) {
      return Array.from({ length: endYear - startYear + 1 }, (v, i) => ({
        label: `${endYear - i}`,
        value: endYear - i,
      }));
    }

    // For other reporting periods, show both years in the range
    return Array.from({ length: endYear - startYear + 1 }, (v, i) => {
      const year = endYear - i;
      const prevYear = year - 1;
      return {
        label: `${prevYear}/${year}`,
        value: year,
      };
    });
  };

  // Memoized year options based on organisation's reporting period
  const yearOptions = useMemo(() => {
    const startYear = years?.[years.length - 1]?.value || new Date().getFullYear();
    let endYear = years?.[0]?.value || new Date().getFullYear();

    // Check if we need to add the next year for organisations with fiscal years
    if (orgDetails?.reportingYearEndDay && orgDetails?.reportingYearEndMonth) {
      const reportingYearEndMonth = orgDetails?.reportingMonth || orgDetails?.reportingYearEndMonth;
      const reportingYearEndDay = orgDetails?.reportingDay || orgDetails?.reportingYearEndDay;
      const currentDate = new Date();
      const currentYearReportingEnd = new Date(endYear, reportingYearEndMonth - 1, reportingYearEndDay);

      // If current date is after the reporting year end, add the next year to options
      if (currentDate > currentYearReportingEnd) {
        endYear = endYear + 1;
      }
    }

    return createYearLabels(startYear, endYear, orgDetails);
  }, [years, orgDetails]);

  useEffect(() => {
    (async () => {
      // User is an ambassador admin and org doesn't have a stripe account -> First sign in, show stripe connect modal.
      if (ambassadorAdmin && currentOrg) {
        // User is an ambassador admin and org doesn't have a stripe account -> First sign in, show stripe connect modal.
        if (!stripeAccountId) {
          setShowStripeConnect(true);
        }
      }
      // Org doesn't have subscription -> show payment modal.
      if (hasSubscriptionActive === false) {
        setShowPaymentModal(true);
      } else {
        setShowPaymentModal(false);
      }
      // If xeroUser and no organisations, show integration modal.
      if (ssoUser && fetchedOrganisations && list?.length === 0) {
        setShowIntegrationModal(true);
      } else {
        setShowIntegrationModal(false);
      }
    })();
    // eslint-disable-next-line
  }, [currentOrg, fetchedOrganisations, ambassadorAdmin, stripeAccountId]);

  useEffect(() => {
    // Recalculate dates based on current year and org details before sending data
    const currentYearForDates = year;
    if (currentYearForDates) {
      const calculatedDates = getReportingYearDates(currentYearForDates, orgDetails, false);
      const multiYearDates = getMultiYearChartDates(currentYear, orgDetails);

      //Treemap and Donut Charts
      let data = [
        {
          group_by: 'entity',
          field: isCalendarYearOrg
            ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
            : [
                `${dataType}.reportingYear.scope.3`,
                `${dataType}.reportingYear.scope.2`,
                `${dataType}.reportingYear.scope.1`,
              ],
          beginningStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
          endStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
          beginningEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
          endEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
          chart: 'yearly',
        },
        //Column Chart
        {
          group_by: isCalendarYearOrg ? `${dataType}.calendarYear.year` : `${dataType}.reportingYear.year`,
          field: isCalendarYearOrg
            ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
            : [
                `${dataType}.reportingYear.scope.3`,
                `${dataType}.reportingYear.scope.2`,
                `${dataType}.reportingYear.scope.1`,
              ],
          beginningStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
          endStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
          beginningEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
          endEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
          chart: 'multiYear',
        },
        //Net Zero Chart to be done later
      ];
      if (canView && currentOrg) {
        sumValues(data, year, currentOrg, dataType);
      }
    }
  }, [canView, year, currentOrg, dataType, userSelectedYear]);

  useEffect(() => {
    if (canView && currentOrg) {
      setEditing(false);
      setUserSelectedYear(false); // Reset user selection when org changes
      getDashboardComponents();
      getTargets();
    }
  }, [canView, currentOrg]);

  useEffect(() => {
    if (dashboardComponents) {
      setComponents([...dashboardComponents?.filter((c) => c.type != 'Cards')]);
      setCardComponents([...dashboardComponents?.filter((c) => c.type === 'Cards')]);
    }
  }, [dashboardComponents]);

  const handleDataType = (event, newDataType) => {
    if (newDataType !== null) {
      setDataType(newDataType);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const {
    btnTextColor,
    dashboardFirstIcon,
    dashboardSecondIcon,
    dashboardThirdIcon,
    dashboardFourthIcon,
    buttonHover,
    dashboardFirstIconBtn,
    dashboardSecondIconBtn,
    dashboardThirdIconBtn,
    dashboardFourthIconBtn,
    dashboardIconBtnTxtColor,
    dashboardFirstIconBtnHover,
    dashboardSecondIconBtnHover,
    dashboardThirdIconBtnHover,
    dashboardForthIconBtnHover,
    dashboardHeaderBgColor,
    dashboardBoxBg,
    dashboardBoxBorder,
    categoryBackground,
    categoryText,
    primaryButton,
    sidebarBackground,
    palette,
    textColor,
  } = branding.colors || {};

  if (stripeCheckoutFailed) return <SubscriptionFailed />;
  if (stripeCheckoutSuccess) return <SubscriptionSuccessful />;

  const showSLAAgreementModal = slaAgreementConfirmed === false && (directUser || whiteLabelAdmin);
  const showEULAAgreementModal = eulaAgreementConfirmed === false && (directUser || whiteLabelAdmin);

  const chipStyle = (selected) => {
    return {
      fontSize: '13px',
      backgroundColor: selected ? primaryButton : '#F3F3F3',
      color: selected ? btnTextColor : '#000',
    };
  };

  const fromYear = new Date(fromDate)?.getFullYear();
  const toYear = new Date(toDate)?.getFullYear();

  const getComponentPos = (id) => components.findIndex((c) => c.id === id);

  const getCardComponentPos = (id) => cardComponents.findIndex((c) => c.id === id);

  const handleDragStart = (event) => {
    setActive(event.active?.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id === over?.id) return;

    setComponents((components) => {
      const originalPos = getComponentPos(active.id);
      const newPos = getComponentPos(over?.id);

      return arrayMove(components, originalPos, newPos);
    });
    setActive(null);
  };

  const handleCardDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id === over?.id) return;

    setCardComponents((components) => {
      const originalPos = getCardComponentPos(active.id);
      const newPos = getCardComponentPos(over?.id);

      return arrayMove(components, originalPos, newPos);
    });
    setActive(null);
  };

  let dataYear = yearOptions.find((y) => y.value === year)?.label;

  const getComponent = (id) => {
    const comp = dashboardComponents.find((c) => c.id === id);
    switch (comp?.type) {
      case 'pieChart':
        if (comp?.name === 'DonutChart') {
          return <DonutChart id={comp?.id} year={dataYear} data={dashboardData} editing={editing} />;
        } else {
          return (
            <PieChart
              key={comp?.id}
              id={comp?.id}
              title={comp?.title || ''}
              data={comp?.data || []}
              valueSuffix={comp?.valueSuffix || ''}
              innerRadius={comp?.innerRadius || 0}
              dataLabels={comp?.dataLabels || false}
              legend={comp?.legend || false}
              colors={palette}
              setDashboardModal={setDashboardModal}
              editing={editing}
            />
          );
        }
      case 'treeMap':
        if (comp?.name === 'TreeMap') {
          return <Treemap id={comp?.id} colors={palette} year={dataYear} data={dashboardData?.treemap} editing={editing} />;
        } else {
          return (
            <TreeMapChart
              key={comp?.id}
              id={comp?.id}
              title={comp?.title || ''}
              data={comp?.data || ''}
              usageTotals={usageTotals || []}
              colors={palette}
              setDashboardModal={setDashboardModal}
              editing={editing}
            />
          );
        }
      case 'coloumnChart':
        return <ColoumnChart id={comp?.id} editing={editing} />;
      case 'netZero':
        return <NetZeroChart id={comp?.id} orgTargets={orgTargets} editing={editing} />;
      case 'Cards':
        return (
          <DashboardCard
            key={comp?.id}
            title={comp?.name ? comp?.name : comp?.title}
            value={
              comp?.name === 'Emissions: Total' ? (
                <span>{dashboardData?.totalEmissions?.toFixed(2)} tCO2e</span>
              ) : comp?.name === 'Emissions: Highest' ? (
                <span>{dashboardData?.highestEmissions?.total?.toFixed(2) || '0'} tCO2e</span>
              ) : comp?.name === 'Emissions: Intensity' ? (
                intensityValue != 'NaN' ? (
                  <span>{intensityValue}</span>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '18px' }}>No Organisation revenue set</span>
                    <AddButton onClick={() => history.push(`/organisations/${currentOrg}`)}>
                      Add Organisation Revenue
                    </AddButton>
                  </div>
                )
              ) : (
                comp?.value
              )
            }
            unit={
              comp?.name === 'Emissions: Total'
                ? (() => {
                    // Calculate percentage change from last year
                    const multiYear = dashboardData?.multiYear;
                    const thisYear = year;
                    const lastYear = year - 1;
                    const thisYearData = multiYear?.[thisYear];
                    const lastYearData = multiYear?.[lastYear];
                    if (thisYearData && lastYearData && lastYearData.total !== 0) {
                      const change = ((thisYearData.total - lastYearData.total) / lastYearData.total) * 100;
                      const isDecrease = change < 0;
                      const percent = Math.abs(change).toFixed(0);
                      return (
                        <span
                          style={{
                            color: 'white',
                            backgroundColor: isDecrease ? 'green' : 'red',
                            opacity: 0.7,
                            border: `1px solid ${isDecrease ? 'green' : 'red'}`,
                            fontSize: '12px',
                            borderRadius: '100px',
                            padding: '3px 5px',
                          }}>
                          <i
                            className={`fa-solid fa-arrow-trend-${isDecrease ? 'down' : 'up'}`}
                            style={{ marginRight: '5px' }}></i>
                          {isDecrease ? `Down ${percent}% from last year` : `Up ${percent}% from last year`}
                        </span>
                      );
                    }
                    return null;
                  })()
                : comp?.name === 'Emissions: Highest'
                ? dashboardData?.highestEmissions?.name
                  ? `${dashboardData?.highestEmissions?.name}`
                  : ''
                : comp?.name === 'Emissions: Intensity'
                ? intensityValue != 'NaN'
                  ? `tCO2e / ${orgCurrency} million`
                  : ''
                : comp?.unit
            }
            userAdded={comp?.userAdded}
            id={comp?.id}
            setDashboardModal={setDashboardModal}
            editing={editing}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className='row'>
        <div className='col-lg-12'>
          <section className='panel'>
            <header
              className='panel-heading dashboard__header'
              style={{
                '--dashboard-header-bg': dashboardHeaderBgColor,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              Welcome {firstName}
              {/*               <div
                style={{
                  display:
                    dashboardComponents &&
                    dashboardComponents?.length > 0 &&
                    dashboardData &&
                    dashboardData?.totalEmissions &&
                    hasSubscriptionActive
                      ? 'flex'
                      : 'none',
                }}>
                <ToggleButton
                  disableRipple
                  disableFocusRipple
                  selected={editing}
                  onChange={() => setEditing(!editing)}
                  sx={{
                    border: `2px solid ${primaryButton}`,
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '5px',
                    textTransform: 'none',
                    backgroundColor: '#F9F9F9',
                    color: '	#1A1A1A',
                    fontFamily: font,
                    transition: 'all 0.2s ease-out',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    '&.Mui-selected': {
                      color: btnTextColor,
                      backgroundColor: `${primaryButton}88`,
                    },
                    '&.Mui-selected:hover': {
                      color: btnTextColor,
                      backgroundColor: `${primaryButton}88`,
                      boxShadow: '0 5px 8px rgba(0, 0, 0, 0.2)',
                      paddingLeft: '25px',
                      i: {
                        opacity: 1,
                        left: '8px',
                      },
                    },
                    i: {
                      transition: 'all 0.2s ease-out',
                      opacity: 0,
                      position: 'absolute',
                      left: '0px',
                    },
                    '&:hover': {
                      color: btnTextColor,
                      backgroundColor: `${primaryButton}88`,
                      boxShadow: '0 5px 8px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                  onClick={() => {
                    if (editing) {
                      let comp = [...cardComponents, ...components];
                      if (JSON.stringify(comp) !== JSON.stringify(dashboardComponents)) {
                        saveComponents(comp);
                      }
                    }
                  }}>
                  {editing ? (
                    <>
                      <i className='fas fa-save'></i> Save Changes
                    </>
                  ) : (
                    'Enable Editing'
                  )}
                </ToggleButton>
              </div> */}
            </header>
          </section>
        </div>
      </div>

      {typeof Dashboard != 'boolean' ||
      typeof canView != 'boolean' ||
      (Dashboard == true &&
        !dashboardData &&
        !dashboardComponents &&
        dashboardComponents?.length !== 0 &&
        hasSubscriptionActive) ? (
        <SkeletonDashboard />
      ) : (
        <>
          {Dashboard && canView ? (
            dashboardData && dashboardData?.totalEmissions != 0 && hasSubscriptionActive ? (
              <>
                {loader && (
                  <div>
                    <LinearProgress color='inherit' style={{ marginBottom: 10 }} />
                  </div>
                )}
                <div>
                  {excludedFacilities?.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      Please note that the following facilities are excluded from the dashboard:{' '}
                      {excludedFacilities?.map((facility) => facility?.name)?.join(', ')}
                    </div>
                  )}
                  <div
                    id='Date and Cards'
                    className='flex-dir'
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      alignItems: 'center',
                      padding: '0px 20px 20px 20px',
                    }}>
                    <div id='Dashboard Cards' className='dashboard-grid-cardbox'>
                      <Card
                        className='dashboard-year-card'
                        variant='outlined'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          padding: '5px',
                          borderRadius: '20px',
                          overflow: 'visible',
                          width: '100%',
                        }}>
                        <h4 style={{ fontWeight: 'bold' }}>Dashboard Year:</h4>
                        <hr style={{ width: '100%', margin: '0px' }} />
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                            margin: '8px',
                          }}>
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ maxWidth: '250px', minWidth: '170px' }}>
                              <Select
                                id='year-select'
                                options={yearOptions}
                                onChange={(selected) => {
                                  const newDates = getReportingYearDates(selected?.value, orgDetails);
                                  setFromDate(newDates.fromDate);
                                  setToDate(newDates.toDate);
                                  setYear(selected?.value);
                                  setUserSelectedYear(true);
                                }}
                                isClearable={false}
                                isSearchable={false}
                                placeholder={'Year'}
                                menuPlacement='auto'
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    fontSize: '18px', // Control text font size
                                    fontWeight: '600',
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    justifyContent: 'center',
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 9999, // Higher z-index to appear above the card
                                  }),
                                }}
                                value={
                                  year
                                    ? yearOptions?.find((yr) => yr?.value === year)
                                    : yearOptions?.find((yr) => yr?.value === dashboardYear)
                                }
                              />
                            </div>
                            {loader && (
                              <div className='loader' style={{ fontSize: '20px', marginLeft: 10 }}>
                                <i className='fa-solid fa-spinner fa-spin'></i>
                              </div>
                            )}
                          </div>
                          <div>
                            <ToggleButtonGroup
                              value={dataType}
                              exclusive
                              onChange={handleDataType}
                              sx={{
                                '& .MuiToggleButton-root': {
                                  backgroundColor: '#bbbfc4', // Default background color
                                  color: '#FFF',
                                  fontFamily: font,
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  textTransform: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  border: 0,
                                  '&.Mui-selected': {
                                    color: btnTextColor, // Text color when selected
                                    backgroundColor: primaryButton, // Background color when selected
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: buttonHover,
                                    color: btnTextColor,
                                  },
                                  '&:hover': {
                                    backgroundColor: buttonHover,
                                    color: btnTextColor,
                                  },
                                },
                              }}>
                              <ToggleButton disableRipple value='market' style={{ marginRight: '10px' }}>
                                Market
                              </ToggleButton>
                              <ToggleButton disableRipple value='location'>
                                Location
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </div>
                        </div>
                      </Card>
                      <DndContext
                        sensors={sensors}
                        collissionsDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleCardDragEnd}>
                        <SortableContext items={cardComponents} strategy={verticalListSortingStrategy}>
                          {cardComponents?.map(
                            (c, i) =>
                              c?.id !== active && (
                                <DashboardCard
                                  key={c?.id}
                                  title={c?.name ? c?.name : c?.title}
                                  value={
                                    c?.name === 'Emissions: Total' ? (
                                      <span>{dashboardData?.totalEmissions?.toFixed(2)} tCO2e</span>
                                    ) : c?.name === 'Emissions: Highest' ? (
                                      <span>{dashboardData?.highestEmissions?.total?.toFixed(2) || '0'} tCO2e</span>
                                    ) : c?.name === 'Emissions: Intensity' ? (
                                      intensityValue != 'NaN' ? (
                                        <span>{intensityValue}</span>
                                      ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                          <span style={{ fontSize: '18px' }}>No Organisation revenue set</span>
                                        </div>
                                      )
                                    ) : (
                                      c?.value
                                    )
                                  }
                                  unit={
                                    c?.name === 'Emissions: Total' ? (
                                      (() => {
                                        // Calculate percentage change from last year
                                        const multiYear = dashboardData?.multiYear;
                                        const thisYear = year;
                                        const lastYear = year - 1;
                                        const thisYearData = multiYear?.[thisYear];
                                        const lastYearData = multiYear?.[lastYear];
                                        if (thisYearData && lastYearData && lastYearData.total !== 0) {
                                          const change =
                                            ((thisYearData.total - lastYearData.total) / lastYearData.total) * 100;
                                          const isDecrease = change < 0;
                                          const percent = Math.abs(change).toFixed(0);
                                          return (
                                            <span
                                              style={{
                                                color: 'white',
                                                backgroundColor: isDecrease ? 'green' : 'red',
                                                opacity: 0.7,
                                                border: `1px solid ${isDecrease ? 'green' : 'red'}`,
                                                fontSize: '12px',
                                                borderRadius: '100px',
                                                padding: '3px 5px',
                                              }}>
                                              <i
                                                className={`fa-solid fa-arrow-trend-${isDecrease ? 'down' : 'up'}`}
                                                style={{ marginRight: '5px' }}></i>
                                              {isDecrease
                                                ? `Down ${percent}% from last year`
                                                : `Up ${percent}% from last year`}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()
                                    ) : c?.name === 'Emissions: Highest' ? (
                                      dashboardData?.highestEmissions?.name ? (
                                        `${dashboardData?.highestEmissions?.name}`
                                      ) : (
                                        ''
                                      )
                                    ) : c?.name === 'Emissions: Intensity' ? (
                                      intensityValue != 'NaN' ? (
                                        `tCO2e / ${orgCurrency} million`
                                      ) : (
                                        <AddButton onClick={() => history.push(`/organisations/${currentOrg}`)}>
                                          Add Organisation Revenue
                                        </AddButton>
                                      )
                                    ) : (
                                      c?.unit
                                    )
                                  }
                                  userAdded={c?.userAdded}
                                  id={c?.id}
                                  setDashboardModal={setDashboardModal}
                                  editing={editing}
                                />
                              )
                          )}
                          {/* <div className='dashboard-add-button'>
                        <Button
                          title='Add Component'
                          id='dashboard-add-button'
                          disableRipple
                          disableFocusRipple
                          sx={{
                            backgroundColor: '#FEFEFE',
                            color: primaryButton,
                            margin: '10px',
                            borderRadius: '50%',
                            width: '50px',
                            height: '50px',
                            minWidth: '50px',
                            transition: 'transform 0.25s ease-out, border-color 0.2s, color 0.2s',
                            '&:hover': {
                              backgroundColor: '#F9F9F9',
                              transition: 'transform 0.25s ease-out, background-color 0.2s, border-color 0.2s, color 0.2s',
                              transform: 'rotate(90deg)',
                              borderColor: '#898989',
                              color: buttonHover,
                            },
                            border: '1px solid #E3E3E3',
                          }}
                          aria-label='add'
                          onClick={() => setDashboardModal({ show: true })}>
                          <AddIcon />
                        </Button>
                      </div> */}
                          <DragOverlay>{active ? <div>{getComponent(active)}</div> : null}</DragOverlay>
                        </SortableContext>
                      </DndContext>
                    </div>
                  </div>

                  <div className='dashboard-grid'>
                    <DndContext
                      sensors={sensors}
                      collissionsDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}>
                      <SortableContext items={components} strategy={verticalListSortingStrategy}>
                        {components?.map((c, i) =>
                          c?.name === 'TreeMap'
                            ? c?.id !== active && (
                                <Treemap
                                  id={c?.id}
                                  colors={palette}
                                  year={dataYear}
                                  data={dashboardData?.treemap}
                                  editing={editing}
                                />
                              )
                            : c?.name === 'ColoumnChart'
                            ? c?.id !== active && <ColoumnChart id={c?.id} editing={editing} />
                            : c?.name === 'DonutChart'
                            ? c?.id !== active && (
                                <DonutChart id={c?.id} year={dataYear} data={dashboardData} editing={editing} />
                              )
                            : c?.name === 'NetZeroChart'
                            ? c?.id !== active &&
                              SBTNetZero &&
                              (orgTargets?.length > 0 ? (
                                <NetZeroChart id={c?.id} orgTargets={orgTargets} editing={editing} />
                              ) : (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: 'white',
                                    borderRadius: '15px',
                                    padding: 20,
                                  }}>
                                  <h3 style={{ textAlign: 'center', fontWeight: '600', marginBottom: 20, marginTop: 0 }}>
                                    No Net Zero Targets Set
                                  </h3>
                                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                    <AddButton onClick={() => history.push('/netZero')}>Add Net Zero Target</AddButton>
                                  </div>
                                </div>
                              ))
                            : c?.type === 'pieChart'
                            ? c?.id !== active && (
                                <>
                                  <PieChart
                                    key={c?.id}
                                    id={c?.id}
                                    title={c?.title || ''}
                                    data={c?.data || []}
                                    valueSuffix={c?.valueSuffix || ''}
                                    innerRadius={c?.innerRadius || 0}
                                    dataLabels={c?.dataLabels || false}
                                    legend={c?.legend || false}
                                    colors={palette}
                                    setDashboardModal={setDashboardModal}
                                    editing={editing}
                                  />
                                </>
                              )
                            : c?.type === 'treeMap'
                            ? c?.id !== active && (
                                <>
                                  <TreeMapChart
                                    key={c?.id}
                                    id={c?.id}
                                    title={c?.title || ''}
                                    data={c?.data || ''}
                                    usageTotals={usageTotals || []}
                                    colors={palette}
                                    setDashboardModal={setDashboardModal}
                                    editing={editing}
                                  />
                                </>
                              )
                            : null
                        )}
                        <DragOverlay>{active ? <div>{getComponent(active)}</div> : null}</DragOverlay>
                      </SortableContext>
                    </DndContext>
                  </div>
                </div>
              </>
            ) : dashboardData && dashboardData?.totalEmissions == 0 ? (
              <div
                className='panel-body'
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '100%',
                  heigth: '90%',
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                }}>
                {years?.length > 0 && (
                  <div
                    id='dashboard-year'
                    style={{
                      display: 'flex',
                      marginBottom: '15px',
                      alignItems: 'center',
                      flexDirection: 'column',
                    }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px', fontSize: '1.3rem', fontWeight: 'bold' }}>
                      Dashboard Year:
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <div style={{ maxWidth: '250px', minWidth: '150px' }}>
                        <Select
                          id='year-select'
                          options={yearOptions}
                          onChange={(selected) => {
                            const newDates = getReportingYearDates(selected?.value, orgDetails);
                            setFromDate(newDates.fromDate);
                            setToDate(newDates.toDate);
                            setYear(selected?.value);
                            setUserSelectedYear(true);
                          }}
                          isClearable={false}
                          isSearchable={false}
                          placeholder={'Year'}
                          menuPlacement='auto'
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontSize: '18px', // Control text font size
                              fontWeight: '600',
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              justifyContent: 'center',
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999, // Higher z-index to appear above the card
                            }),
                          }}
                          value={
                            !year
                              ? yearOptions?.find((yr) => yr?.value === dashboardYear)
                              : yearOptions?.find((yr) => yr?.value === year)
                          }
                        />
                      </div>
                      {loader && (
                        <div className='loader' style={{ fontSize: '20px', marginLeft: 10 }}>
                          <i className='fa-solid fa-spinner fa-spin'></i>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h2 style={{ marginTop: '10px' }}>No Data Available Yet!</h2>
                  <span style={{ marginBottom: '10px' }}>Go to the Usage Data page to add some data</span>
                  <AddButton onClick={() => history.push('/usage')} style={{ marginBottom: '10px' }}>
                    Add Usage Data
                  </AddButton>
                </div>
              </div>
            ) : !dashboardData && !dashboardComponents && dashboardComponents?.length !== 0 && hasSubscriptionActive ? (
              <SkeletonDashboard />
            ) : (
              !hasSubscriptionActive && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Alert severity='warning' style={{ fontSize: '1.2rem', border: '1px #CCC solid', fontFamily: 'inherit' }}>
                    You do not have an active subscription. Please subscribe to view the dashboard.
                  </Alert>
                </div>
              )
            )
          ) : Dashboard == false || canView != true ? (
            <div
              style={{
                height: window.screen.availHeight > 1078 ? '78vh' : '75vh',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}>
              <div className='row' id='dashboard__icons'>
                <div className='col-md-3' id='dashboard__organisations'>
                  <div
                    className='mini-stat clearfix dashboard__mini-stat'
                    style={{ '--dashboard-box-bg': dashboardBoxBg, '--dashboard-box-border': dashboardBoxBorder }}>
                    <DashboardIcon className='mini-stat-icon' bgcolor={dashboardFirstIcon}>
                      <i className='fa fa-building' />
                    </DashboardIcon>
                    <div className='mini-stat-info' style={{ marginLeft: '2vmin', fontSize: 14 }}>
                      Organisations
                    </div>
                  </div>

                  <DashboardButton
                    className='btn btn-xs'
                    bgcolor={dashboardFirstIconBtn || dashboardFirstIcon}
                    bgcolorhover={dashboardFirstIconBtnHover || buttonHover}
                    color={dashboardIconBtnTxtColor || btnTextColor}
                    to='/organisations'
                    style={{ fontSize: 14 }}>
                    Manage Organisations
                  </DashboardButton>
                </div>

                <div className='col-md-3' id='dashboard__facilities'>
                  <div
                    className='mini-stat clearfix dashboard__mini-stat'
                    style={{ '--dashboard-box-bg': dashboardBoxBg, '--dashboard-box-border': dashboardBoxBorder }}>
                    <DashboardIcon className='mini-stat-icon' bgcolor={dashboardSecondIcon}>
                      <i className='fa fa-map-marker' />
                    </DashboardIcon>

                    <div className='mini-stat-info' style={{ marginLeft: '2vmin', fontSize: 14 }}>
                      Facilities
                    </div>
                  </div>

                  <DashboardButton
                    className='btn btn-xs'
                    bgcolor={dashboardSecondIconBtn || dashboardSecondIcon}
                    bgcolorhover={dashboardSecondIconBtnHover || buttonHover}
                    color={dashboardIconBtnTxtColor || btnTextColor}
                    to='/facilities'
                    style={{ fontSize: 14 }}>
                    Manage Facilities
                  </DashboardButton>
                </div>

                <div className='col-md-3' id='dashboard__usageData'>
                  <div
                    className='mini-stat clearfix dashboard__mini-stat'
                    style={{ '--dashboard-box-bg': dashboardBoxBg, '--dashboard-box-border': dashboardBoxBorder }}>
                    <DashboardIcon className='mini-stat-icon' bgcolor={dashboardThirdIcon}>
                      <i className='fa fa-bar-chart' />
                    </DashboardIcon>
                    <div className='mini-stat-info' style={{ marginLeft: '2vmin', fontSize: 14 }}>
                      Data Entries
                    </div>
                  </div>

                  <DashboardButton
                    className='btn btn-xs'
                    bgcolor={dashboardThirdIconBtn || dashboardThirdIcon}
                    bgcolorhover={dashboardThirdIconBtnHover || buttonHover}
                    color={dashboardIconBtnTxtColor || btnTextColor}
                    to='/usage'
                    style={{ pointerEvents: blockBtns ? 'none' : 'auto', fontSize: 14 }}>
                    Manage Usage Data
                  </DashboardButton>
                </div>

                <div className='col-md-3' id='dashboard__reports'>
                  <div
                    className='mini-stat clearfix dashboard__mini-stat'
                    style={{ '--dashboard-box-bg': dashboardBoxBg, '--dashboard-box-border': dashboardBoxBorder }}>
                    <DashboardIcon className='mini-stat-icon' bgcolor={dashboardFourthIcon}>
                      <i className='fa fa-file-text' />
                    </DashboardIcon>
                    <div className='mini-stat-info' style={{ marginLeft: '2vmin', fontSize: 14 }}>
                      Reports
                    </div>
                  </div>

                  <DashboardButton
                    className='btn btn-xs'
                    bgcolor={dashboardFourthIconBtn || dashboardFourthIcon}
                    bgcolorhover={dashboardForthIconBtnHover || buttonHover}
                    color={dashboardIconBtnTxtColor || btnTextColor}
                    to='/reports'
                    style={{ pointerEvents: blockBtns ? 'none' : 'auto', fontSize: 14 }}>
                    Manage Reports
                  </DashboardButton>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='row' id='clientTestOrgs__alertMessage'>
          <div style={{ display: accountType?.includes('Client Test Organisation') ? 'block' : 'none' }}>
            <Alert
              severity='info'
              style={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
                border: '1px solid grey',
                marginLeft: '15px',
              }}>
              <p
                style={{
                  fontSize: 16,
                  marginTop: '5px',
                  marginBottom: '5px',
                }}>
                Test Organisation expires in: {daysUntilTestOrgExpire} days.
              </p>
            </Alert>
          </div>
        </div>
        <div id='dashboard__helpHero_btn'>
          <Button
            variant='contained'
            startIcon={<HelpOutline />}
            style={helpHeroBtnStyles}
            onClick={() => {
              hlp.anonymous();
              hlp.startTour(String(tourID));
            }}>
            Help
          </Button>
        </div>
      </div>

      {ssoUser && showIntegrationModal && (
        <IntegrationModal setShowIntegrationModal={setShowIntegrationModal} showIntegrationModal={showIntegrationModal} />
      )}

      <ConnectStripeAccountModal showStripeConnect={showStripeConnect} connectStripeAccount={connectStripeAccount} />
      {showSLAAgreementModal && !showPaymentModal && !showIntegrationModal && <OrgAgreementsModal />}
      {showEULAAgreementModal && !showPaymentModal && !showIntegrationModal && <UserAgreementsModal />}
      {/* Display SubscriptionSuccessful Modal here if user updates subscription under dashboard component*/}
      {showSubscriptionSuccesfulModal && <SubscriptionSuccessful setShowPaymentModal={setShowPaymentModal} />}
      {showPaymentModal &&
        !hasSubscriptionActive &&
        (canManage ? (
          <Register setShowPaymentModal={setShowPaymentModal} />
        ) : (
          <SubscriptionEnded setShowPaymentModal={setShowPaymentModal} />
        ))}
      {dashboardModal.show && <DashboardModal dashboardModal={dashboardModal} setDashboardModal={setDashboardModal} />}
    </>
  );
};

const mapStateToProps = (state) => {
  const { firstName, ambassadorAdmin, cyfAdmin, whiteLabelAdmin, consultantAdmin, ssoUser } = state?.profile?.details || {};
  const { softwareLicenseAgreementConfirmed, userLicenseAgreementConfirmed } = state.profile?.details?.legalAgreements || {};
  const usageTotals = state?.usageTotals;

  const { hasSubscriptionActive, stripeAccountId, accountType, creationDate } = state.organisation?.details || {};
  const directUser = consultantAdmin === false && cyfAdmin === false && ambassadorAdmin === false;
  const { orgMissingCompulsoryData, list, fetchedOrganisations } = state?.organisation || {};
  const blockBtns = !cyfAdmin && (!hasSubscriptionActive || orgMissingCompulsoryData);
  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const SBTfeature = features?.find((feature) => feature.featureKey === 'SCIENCE_BASED_TARGETS');
  const dashboardFeature = features?.find((feature) => feature.featureKey === 'DASHBOARD');
  const SBTNetZero = availableFeatures?.includes('SCIENCE_BASED_TARGETS') || SBTfeature?.enabledByDefault;
  const Dashboard = availableFeatures?.includes('DASHBOARD') || dashboardFeature?.enabledByDefault;
  const currencies = state?.organisation?.currencies;
  const orgCurrency = currencies?.find((c) => c.id === state?.organisation?.details?.currency)?.isoCode;
  const hostname = window.location.hostname;

  let daysUntilTestOrgExpire = 0;
  if (accountType?.includes('Client Test Organisation') && creationDate) {
    const expireDay = addDays(new Date(creationDate), 30);
    daysUntilTestOrgExpire = differenceInDays(expireDay, new Date()) + 1;
    daysUntilTestOrgExpire = daysUntilTestOrgExpire < 0 ? 0 : daysUntilTestOrgExpire;
  }

  const helpHeroBtnStyles = {
    background: state.branding?.colors?.primaryColor,
    width: 93,
    borderRadius: 9,
    fontWeight: 600,
    padding: '10px 15px 10px 15px',
    fontSize: 15,
    display: ['localhost', 'compareyourfootprint'].some((x) => hostname.includes(x)) ? 'flex' : 'none',
  };

  const startYear = state?.dashboard?.years?.startYear;
  const endYear = state?.dashboard?.years?.endYear;

  // Smart year determination logic using latestUsage
  const orgDetails = state?.organisation?.details;
  const latestUsage = state?.organisation?.details?.usageYears?.maxEndDate;

  let dashboardYear = endYear; // Default fallback

  if (latestUsage) {
    const latestUsageDate = new Date(latestUsage);
    const latestUsageYear = latestUsageDate.getFullYear();

    // For reporting year organizations, determine the correct year
    let effectiveYear = latestUsageYear;

    if (orgDetails?.reportingYearEndDay && orgDetails?.reportingYearEndMonth) {
      const reportingYearEndMonth = orgDetails?.reportingMonth || orgDetails?.reportingYearEndMonth;
      const reportingYearEndDay = orgDetails?.reportingDay || orgDetails?.reportingYearEndDay;
      const latestUsageReportingEnd = new Date(latestUsageYear, reportingYearEndMonth - 1, reportingYearEndDay);

      const isCalendarYearOrg = orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;

      // If latest usage date is before the reporting year end, use the previous year
      if (latestUsageDate > latestUsageReportingEnd && !isCalendarYearOrg) {
        effectiveYear = latestUsageYear + 1;
      }
    }

    dashboardYear = effectiveYear;
  }

  const years = Array.from({ length: endYear - startYear + 1 }, (v, i) => ({
    label: `${endYear - i}`,
    value: endYear - i,
  }));

  return {
    slaAgreementConfirmed: softwareLicenseAgreementConfirmed,
    eulaAgreementConfirmed: userLicenseAgreementConfirmed,
    currentOrg: state.currentOrganisation,
    firstName,
    ssoUser,
    ambassadorAdmin,
    whiteLabelAdmin,
    directUser,
    stripeAccountId,
    list,
    fetchedOrganisations,
    branding: state.branding || {},
    hasSubscriptionActive,
    showSubscriptionSuccesfulModal: state.payment && state.payment.subscriptionSucessfull,
    accountType,
    daysUntilTestOrgExpire,
    helpHeroBtnStyles,
    stripeCheckoutFailed: window.location.pathname.includes('cancel'),
    stripeCheckoutSuccess: window.location.pathname.includes('success'),
    blockBtns,
    dashboardYear,
    latestUsage: state?.organisation?.details?.usageYears?.maxEndDate,
    years,
    dashboardComponents: state?.dashboard?.components,
    dashboardData: state?.dashboard?.data,
    loader: state?.showLoader,
    currentYear: new Date().getFullYear(),
    usageTotals,
    orgTargets: state?.dashboard?.targets,
    SBTNetZero: SBTNetZero || false,
    Dashboard: Dashboard || false,
    canView: !!state?.organisation?.permissions?.dashboard?.view,
    canEdit: !!state?.organisation?.permissions?.dashboard?.edit,
    canCreate: !!state?.organisation?.permissions?.dashboard?.create,
    canDelete: !!state?.organisation?.permissions?.dashboard?.delete,
    canManage: !!state?.organisation?.permissions?.organisation?.manage,
    orgDetails: state?.organisation?.details,
    excludedFacilities: state?.facility?.list?.filter((facility) => facility?.excludeFromReports),
    orgMetrics: state?.organisation?.metrics?.filter((m) => m.organisationId === state?.organisation?.details?.id),
    orgCurrency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getLicenses: () => dispatch(ACTIONS.getLicenses()),
  connectStripeAccount: () => dispatch(PROFILEACTIONS.connectStripeAccount()),
  sumValues: (data, year, org, dataType) => dispatch(DASHBOARDACTIONS.sumValues(data, year, org, dataType)),
  getUsageTotals: () => dispatch(USAGEACTIONS.getUsageTotals()),
  getTargets: () => dispatch(DASHBOARDACTIONS.getTargets()),
  getDashboardComponents: () => dispatch(DASHBOARDACTIONS.getDashboardComponents()),
  saveComponents: (components) => dispatch(DASHBOARDACTIONS.saveComponents(components)),
  showLoader: (status) => dispatch(ACTIONS.showLoader(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
